import type {
  ObPlanId,
  BillingInterval,
  DiscountScope,
  DiscountDuration,
  DiscountType,
} from '../../gql/graphql'

/**
 * Information about a concrete plan
 */
export interface PlanInfo {
  planId: ObPlanId
  planName: string
  channelCount: number
  billingCyclePrice: number
  billingCycle: BillingInterval
  pricePerChannel: number
}

/**
 * Calculated pricing information for a plan with specific channel count and billing cycle
 */
export interface PlanCalculation {
  basePrice: number
  totalPrice: number
  savings?: number
  pricePerChannel: number
  discountValue?: number
  originalPrice?: number
}

export type OrganizationId = string

// Const objects with union type values (no type assertions needed)
export const OBPlanIdValues = {
  free: 'free' as ObPlanId,
  essentials: 'essentials' as ObPlanId,
  team: 'team' as ObPlanId,
  agency: 'agency' as ObPlanId,
} as const

export const BillingIntervalValues = {
  month: 'month' as BillingInterval,
  year: 'year' as BillingInterval,
} as const

export const DiscountTypeValues = {
  percent: 'percent' as DiscountType,
  amount: 'amount' as DiscountType,
} as const

export const DiscountDurationValues = {
  forever: 'forever' as DiscountDuration,
  once: 'once' as DiscountDuration,
  repeating: 'repeating' as DiscountDuration,
} as const

export const DiscountScopeValues = {
  customer: 'customer' as DiscountScope,
  subscription: 'subscription' as DiscountScope,
} as const

export enum UpdateType {
  ChannelIncrease = 'channel_increase',
  ChannelDecrease = 'channel_decrease',
  SubscriptionUpgrade = 'subscription_upgrade',
  SubscriptionDowngrade = 'subscription_downgrade',
  SubscriptionUpdate = 'subscription_update',
}

/**
 * Phases of the plan update flow
 */
export enum UpdatePlanPhase {
  /** No update in progress */
  Idle = 'idle',
  /** Updating subscription on backend */
  Updating = 'updating',
  /** Authorizing payment with Stripe (3D Secure/SCA) */
  Authorizing = 'authorizing',
  /** Confirming payment after 3D Secure success (refetching, about to show success) */
  Confirming = 'confirming',
}
